// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import InvoiceDetailsBillingAddress, {
    InvoiceDetailsBillingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsBillingAddress";
import InvoiceDetailsShippingAddress, {
    InvoiceDetailsShippingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsShippingAddress";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps>;

export interface InvoiceDetailsInformationStyles {
    invoiceInformationGridContainer?: GridContainerProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    informationGridItem?: GridItemProps;
    invoiceInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    shippingMethodGridItem?: GridItemProps;
    invoiceDateGridItem?: GridItemProps;
    termsGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    invoiceNotesGridItem?: GridItemProps;
    billingAddressStyle?: InvoiceDetailsBillingAddressStyles;
    shippingAddressStyle?: InvoiceDetailsShippingAddressStyles;
    // XNG-Changes
    linkStyle?: LinkPresentationProps;
}

export const informationStyles: InvoiceDetailsInformationStyles = {
    invoiceInformationGridContainer: {
        css: css`
            padding: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    informationGridItem: {
        width: 12,
    },
    invoiceInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    invoiceDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    termsGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    shippingMethodGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    vatNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    invoiceNotesGridItem: {
        width: 12,
    },
};

const styles = informationStyles;

const InvoiceDetailsInformation = ({ language }: Props) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    if (!invoice) {
        return null;
    }

    return (
        <GridContainer {...styles.invoiceInformationGridContainer} data-test-selector="invoiceDetail_information">
            <GridItem {...styles.informationGridItem}>
                <StyledWrapper {...styles.informationGridContainer}>
                    <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Invoice Date")}
                            text={getLocalizedDateTime({
                                dateTime: new Date(invoice.invoiceDate),
                                language,
                            })}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.poNumberGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PO #")}
                            text={invoice.customerPO}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Status")}
                            text={invoice.status}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.shippingMethodGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Ship Via")}
                            text={invoice.shipViaDescription || invoice.shipCode}
                        />
                    </StyledWrapper>
                    {/* <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.termsGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Terms")}
                            text={invoice.terms}
                        />
                    </GridItem> */}
                    <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.dueDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Due Date")}
                            text={getLocalizedDateTime({
                                dateTime: new Date(invoice.dueDate),
                                language,
                            })}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.dueDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Balance Due")}
                            text={invoice.currentBalanceDisplay}
                        />
                    </StyledWrapper>
                    {invoice.customerVatNumber && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("VAT Number")}
                                text={invoice.customerVatNumber}
                            />
                        </StyledWrapper>
                    )}
                    {/*  XNG-Changes: 
                    classic extra data -
                    (shipDate ,salesperson,customerNo, upsTrackingNumber, releaseNumber, writer, terms, orderedBy ) */}
                    {invoice.properties?.shipDate && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Ship Date")}
                                text={invoice.properties?.shipDate}
                            />
                        </StyledWrapper>
                    )}
                    {invoice.properties?.salesperson && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Salesperson")}
                                text={invoice.properties?.salesperson}
                            />
                        </StyledWrapper>
                    )}
                    {invoice.properties.customerNo && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Customer Number")}
                                text={invoice.properties.customerNo}
                            />
                        </StyledWrapper>
                    )}
                    {invoice.properties?.upsTrackingNumber && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <StyledWrapper {...styles.headingAndTextStyles?.wrapper}>
                                <Typography {...styles.headingAndTextStyles?.heading}>
                                    {translate("Tracking Number")}
                                </Typography>
                                <Link
                                    target="_blank"
                                    href={invoice.properties?.trackingNumberUrl}
                                    {...styles.linkStyle}
                                >
                                    {invoice.properties?.upsTrackingNumber}
                                </Link>
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {invoice.properties?.releaseNumber && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Release #")}
                                text={invoice.properties?.releaseNumber}
                            />
                        </StyledWrapper>
                    )}
                    {invoice.properties?.writer && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Writer")}
                                text={invoice.properties?.writer}
                            />
                        </StyledWrapper>
                    )}
                    {invoice.properties?.terms && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Terms")}
                                text={invoice.properties?.terms}
                            />
                        </StyledWrapper>
                    )}
                    {invoice.properties?.orderedBy && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Ordered By")}
                                text={invoice.properties?.orderedBy}
                            />
                        </StyledWrapper>
                    )}
                    {/* XNG-Changes: move notes under top information */}
                    {invoice.notes && (
                        <StyledWrapper {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceNotesGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Invoice Notes")}
                                text={invoice.notes}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </GridItem>
            <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.shippingAddressGridItem)}>
                <InvoiceDetailsShippingAddress invoice={invoice} extendedStyles={styles.shippingAddressStyle} />
            </GridItem>
            <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.billingAddressGridItem)}>
                <InvoiceDetailsBillingAddress invoice={invoice} extendedStyles={styles.billingAddressStyle} />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InvoiceDetailsInformation),
    definition: {
        group: "Invoice Details",
        displayName: "Information",
        allowedContexts: ["InvoiceDetailsPage"],
    },
};

export default widgetModule;
